import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { Loading } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, today } from "@/lib/format";

const TABS = [["profile", "Profile"], ["products", "Linked products"],
  ["purchases", "Purchase history"], ["ledger", "Ledger"], ["documents", "Documents"]];

const Row = ({ label, value }) => (
  <div>
    <p className="overline text-slate-400">{label}</p>
    <p className="mt-1.5 text-sm text-slate-800 break-words">{value || "—"}</p>
  </div>
);

const emptyPayment = () => ({ amount: "", mode: "bank_transfer", reference: "", paid_on: today(), notes: "" });

export function SupplierDetail({ supplierId, onClose, onEdit, onChanged }) {
  const { can } = useAuth();
  const [tab, setTab] = useState("profile");
  const [data, setData] = useState(null);
  const [payment, setPayment] = useState(emptyPayment());
  const [payOpen, setPayOpen] = useState(false);
  const fileRef = useRef(null);
  const [docType, setDocType] = useState("gst_certificate");
  const [uploading, setUploading] = useState(false);
  const [allMeds, setAllMeds] = useState([]);
  const [link, setLink] = useState({ medicine_id: "", supplier_code: "", pack: "",
    purchase_price: "", mrp: "" });

  const load = useCallback(async () => {
    if (!supplierId) return;
    setData(null);
    try {
      const res = await api.get(`/pharmacy/suppliers/${supplierId}`);
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); onClose(); }
  }, [supplierId, onClose]);

  useEffect(() => { setTab("profile"); load(); }, [load]);

  useEffect(() => {
    if (!supplierId) return;
    api.get("/pharmacy/medicines", { params: { limit: 500 } })
      .then(({ data }) => setAllMeds(data.items)).catch(() => {});
  }, [supplierId]);

  const linkMedicine = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pharmacy/suppliers/${supplierId}/medicines`, {
        medicine_id: link.medicine_id, supplier_code: link.supplier_code, pack: link.pack,
        purchase_price: Number(link.purchase_price || 0), mrp: Number(link.mrp || 0) });
      toast.success("Supplier medicine saved.");
      setLink({ medicine_id: "", supplier_code: "", pack: "", purchase_price: "", mrp: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const unlinkMedicine = async (medicineId) => {
    try {
      await api.delete(`/pharmacy/suppliers/${supplierId}/medicines/${medicineId}`);
      toast.success("Supplier medicine removed.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const s = data?.supplier;
  const ledger = data?.ledger;

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pharmacy/suppliers/${supplierId}/payments`,
        { ...payment, amount: Number(payment.amount || 0) });
      toast.success("Payment recorded against this supplier.");
      setPayOpen(false); setPayment(emptyPayment()); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    body.append("doc_type", docType);
    setUploading(true);
    try {
      await api.post(`/pharmacy/suppliers/${supplierId}/documents`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      load();    } catch (err) { toast.error(apiError(err)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/pharmacy/suppliers/${supplierId}/documents/${doc.document_id}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteDoc = async (doc) => {
    try {
      await api.delete(`/pharmacy/suppliers/${supplierId}/documents/${doc.document_id}`);
      toast.success("Document removed.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal open={Boolean(supplierId)} onClose={onClose} wide title={s?.name || "Supplier"}
      subtitle={s ? `${(s.status || "active") === "active" ? "Active" : "Inactive"} · ${s.city || "—"}` : ""}
      testid="supplier-detail-modal">
      {!data ? <Loading /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`supplier-tab-${v}`}
                className={`rounded-full px-4 py-2 text-sm font-medium ${tab === v
                  ? "bg-[#0F172A] text-white" : "border border-slate-300 text-slate-600 hover:border-slate-900"}`}>
                {l}
              </button>
            ))}
            {can("pharmacy.suppliers.manage") && (
              <button onClick={() => onEdit(s)} className={ghostBtnCls} data-testid="supplier-detail-edit-btn">Edit</button>
            )}
          </div>

          {tab === "profile" && (
            <div className="space-y-6" data-testid="supplier-profile-panel">
              <div className="grid gap-5 sm:grid-cols-3">
                <Row label="Contact person" value={s.contact_person} />
                <Row label="Mobile" value={s.phone} />
                <Row label="Alternate phone" value={s.alt_phone} />
                <Row label="Email" value={s.email} />
                <Row label="Category" value={(s.category || "").replace(/_/g, " ")} />
                <Row label="Address" value={[s.address, s.city, s.state, s.pincode, s.country].filter(Boolean).join(", ")} />
                <Row label="GSTIN" value={s.gstin} />
                <Row label="PAN" value={s.pan} />
                <Row label="Drug licence" value={s.drug_license_no ? `${s.drug_license_no} (${s.drug_license_type || "—"})` : ""} />
                <Row label="Licence expiry" value={s.drug_license_expiry ? dmy(s.drug_license_expiry) : ""} />
                <Row label="Payment terms" value={s.payment_terms_days ? `${s.payment_terms_days} days` : ""} />
                <Row label="Credit limit" value={s.credit_limit ? inr(s.credit_limit) : ""} />
                <Row label="Bank" value={s.bank_name} />
                <Row label="Account holder" value={s.account_holder} />
                <Row label="Account no." value={s.account_no} />
                <Row label="IFSC" value={s.ifsc} />
                <Row label="UPI / payment ID" value={s.upi_id} />
                <Row label="Notes" value={s.notes} />
              </div>
              {(s.status || "active") !== "active" && (
                <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="supplier-inactive-note">
                  Deactivated{s.status_reason ? ` — ${s.status_reason}` : ""}. Historical records are intact;
                  this supplier will not appear in new purchase entry.
                </p>
              )}
            </div>
          )}

          {tab === "products" && (
            <div data-testid="supplier-products-panel">
              {can("pharmacy.medicines.edit") && (
                <form onSubmit={linkMedicine} className="mb-5 grid gap-3 sm:grid-cols-6">
                  <select required className={`${inputCls} sm:col-span-2`} value={link.medicine_id}
                    data-testid="supplier-link-medicine-select"
                    onChange={(e) => setLink({ ...link, medicine_id: e.target.value })}>
                    <option value="">Select medicine</option>
                    {allMeds.map((m) => (
                      <option key={m.medicine_id} value={m.medicine_id}>{m.code} · {m.name}</option>
                    ))}
                  </select>
                  <input placeholder="Supplier product code" className={inputCls} value={link.supplier_code}
                    data-testid="supplier-link-code-input"
                    onChange={(e) => setLink({ ...link, supplier_code: e.target.value })} />
                  <input placeholder="Pack" className={inputCls} value={link.pack}
                    onChange={(e) => setLink({ ...link, pack: e.target.value })} />
                  <input type="number" min="0" placeholder="Purchase price" className={inputCls}
                    value={link.purchase_price} data-testid="supplier-link-price-input"
                    onChange={(e) => setLink({ ...link, purchase_price: e.target.value })} />
                  <input type="number" min="0" placeholder="MRP" className={inputCls} value={link.mrp}
                    onChange={(e) => setLink({ ...link, mrp: e.target.value })} />
                  <div className="sm:col-span-6">
                    <button className={primaryBtnCls} data-testid="supplier-link-submit-btn">
                      Add / update supplier medicine
                    </button>
                  </div>
                </form>
              )}
              {(data.supplier_links || []).length > 0 && (
                <div className="mb-5 overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="px-4 py-3">Medicine</th><th className="px-4 py-3">Supplier code</th>
                        <th className="px-4 py-3">Pack</th><th className="px-4 py-3 text-right">Rate</th>
                        <th className="px-4 py-3 text-right">Last rate</th><th className="px-4 py-3 text-right">MRP</th>
                        <th className="px-4 py-3 text-right">GST</th><th className="px-4 py-3" /></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.supplier_links.map((l) => (
                        <tr key={l.medicine_id} data-testid={`supplier-medicine-${l.medicine_id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            <span className="font-mono-num mr-2 text-slate-500">{l.medicine_code}</span>{l.medicine_name}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{l.supplier_code || "—"}</td>
                          <td className="px-4 py-3 text-slate-600">{l.pack || "—"}</td>
                          <td className="px-4 py-3 text-right">{inr(l.purchase_price)}</td>
                          <td className="px-4 py-3 text-right text-slate-500">{inr(l.last_purchase_price)}</td>
                          <td className="px-4 py-3 text-right">{inr(l.mrp)}</td>
                          <td className="px-4 py-3 text-right text-slate-500">{l.gst_percent}%</td>
                          <td className="px-4 py-3 text-right">
                            {can("pharmacy.medicines.edit") && (
                              <button onClick={() => unlinkMedicine(l.medicine_id)}
                                data-testid={`unlink-medicine-${l.medicine_id}`}
                                className="text-xs font-medium text-red-600 hover:text-red-800">Remove</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {data.linked_products.length === 0
                ? <p className="text-sm text-slate-500">No products linked yet. Products link automatically when purchased from this supplier.</p>
                : (
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {data.linked_products.map((p) => (
                      <li key={p.medicine_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                        data-testid={`supplier-product-${p.medicine_id}`}>
                        <span className="font-medium text-slate-900">{p.name}
                          <span className="ml-2 text-xs text-slate-400">{p.generic_name || p.unit}</span></span>
                        <span className="text-slate-600">MRP {inr(p.mrp)} · Stock {p.stock ?? 0}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          )}

          {tab === "purchases" && (
            <div data-testid="supplier-purchases-panel">
              {data.purchases.length === 0 ? <p className="text-sm text-slate-500">No purchases recorded for this supplier.</p> : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {data.purchases.map((p) => (
                    <li key={p.purchase_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                      data-testid={`supplier-purchase-${p.purchase_id}`}>
                      <span>
                        <span className="font-mono-num font-medium text-slate-900">{p.purchase_no}</span>
                        <span className="ml-2 text-slate-500">{dmy(p.purchase_date || p.created_at)}</span>
                        <span className="ml-2 text-xs uppercase tracking-wider text-slate-400">{p.status === "draft" ? "Draft" : "Final"}</span>
                        {p.supplier_invoice_no && <span className="ml-2 text-slate-500">Inv {p.supplier_invoice_no}</span>}
                      </span>
                      <span className="font-medium text-slate-900">{inr(p.total)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "ledger" && (
            <div className="space-y-5" data-testid="supplier-ledger-panel">
              <div className="grid gap-3 sm:grid-cols-4 rounded-lg bg-[#0F172A] p-5 text-white">
                {[["Purchased", inr(ledger.purchased), "ledger-purchased"],
                  ["Returns", inr(ledger.returned), "ledger-returned"],
                  ["Paid", inr(ledger.paid), "ledger-paid"]].map(([k, v, t]) => (
                  <div key={k}><p className="overline text-slate-400">{k}</p>
                    <p className="mt-1 font-semibold" data-testid={t}>{v}</p></div>
                ))}
                <div><p className="overline text-[#EA580C]">Outstanding</p>
                  <p className="mt-1 font-ui text-xl font-bold" data-testid="ledger-outstanding">{inr(ledger.outstanding)}</p></div>
              </div>
              {can("pharmacy.supplier.payments") && (
                <button onClick={() => setPayOpen(true)} className={primaryBtnCls} data-testid="record-payment-btn">
                  Record payment
                </button>
              )}
              {ledger.entries.length === 0 ? <p className="text-sm text-slate-500">No ledger entries yet.</p> : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Particulars</th>
                        <th className="px-4 py-3 text-right">Debit</th><th className="px-4 py-3 text-right">Credit</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ledger.entries.map((e, i) => (
                        <tr key={i} data-testid={`ledger-entry-${i}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-slate-500">{dmy(e.date)}</td>
                          <td className="px-4 py-3 text-slate-800">{e.particulars}</td>
                          <td className="px-4 py-3 text-right">{e.debit ? inr(e.debit) : "—"}</td>
                          <td className="px-4 py-3 text-right">{e.credit ? inr(e.credit) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "documents" && (
            <div className="space-y-5" data-testid="supplier-documents-panel">
              {can("pharmacy.suppliers.manage") && (
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="Document type">
                    <select className={`${inputCls} sm:!w-52`} value={docType} data-testid="doc-type-select"
                      onChange={(e) => setDocType(e.target.value)}>
                      <option value="gst_certificate">GST certificate</option>
                      <option value="drug_licence">Drug licence</option>
                      <option value="pan">PAN card</option>
                      <option value="agreement">Agreement</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                    data-testid="doc-file-input" onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className={primaryBtnCls} data-testid="doc-upload-btn">
                    <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload document"}
                  </button>
                  <p className="text-xs text-slate-500">PDF, JPG, PNG or WEBP up to 5 MB.</p>
                </div>
              )}
              {(s.documents || []).length === 0 ? <p className="text-sm text-slate-500">No documents uploaded.</p> : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {(s.documents || []).map((d) => (
                    <li key={d.document_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                      data-testid={`supplier-doc-${d.document_id}`}>
                      <button onClick={() => openDoc(d)} className="inline-flex items-center gap-2 font-medium text-slate-900 hover:underline">
                        <FileText className="h-4 w-4 text-slate-400" /> {d.filename}
                      </button>
                      <span className="flex items-center gap-4 text-xs text-slate-500">
                        {(d.doc_type || "").replace(/_/g, " ")} · {dmy(d.uploaded_at)}
                        {can("pharmacy.suppliers.manage") && (
                          <button onClick={() => deleteDoc(d)} data-testid={`delete-doc-${d.document_id}`}
                            className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!data.can_delete && (
            <p className="text-xs text-slate-500" data-testid="supplier-usage-note">
              Linked records: {data.usage.purchases} purchase(s), {data.usage.batches} batch(es),
              {" "}{data.usage.purchase_returns} return(s), {data.usage.payments} payment(s) —
              this supplier can be deactivated but not permanently deleted.
            </p>
          )}
        </div>
      )}

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record supplier payment"
        subtitle={s?.name} testid="supplier-payment-modal">
        <form onSubmit={recordPayment} className="grid gap-5 sm:grid-cols-2">
          <Field label="Amount (₹) *">
            <input required type="number" min="1" step="0.01" className={inputCls} value={payment.amount}
              data-testid="payment-amount-input" onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
          </Field>
          <Field label="Mode">
            <select className={inputCls} value={payment.mode} data-testid="payment-mode-select"
              onChange={(e) => setPayment({ ...payment, mode: e.target.value })}>
              {["bank_transfer", "cash", "upi", "cheque", "card", "other"].map((m) => (
                <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
              ))}
            </select>
          </Field>
          <Field label="Reference / UTR">
            <input className={inputCls} value={payment.reference} data-testid="payment-reference-input"
              onChange={(e) => setPayment({ ...payment, reference: e.target.value })} />
          </Field>
          <Field label="Paid on">
            <input type="date" className={inputCls} value={payment.paid_on} data-testid="payment-date-input"
              onChange={(e) => setPayment({ ...payment, paid_on: e.target.value })} />
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <input className={inputCls} value={payment.notes}
              onChange={(e) => setPayment({ ...payment, notes: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="payment-submit-btn">Save payment</button>
          </div>
        </form>
      </Modal>
    </Modal>
  );
}
